import {
  Stripe,
  StripeElements,
  StripeCardElementChangeEvent,
} from "@stripe/stripe-js";
import { notifySuccess } from "pages/Errors/Error";
import { RefObject } from "react";
import { StripeCard } from "../types/types";
import { dispatchCountryBitsSold } from "utils/countryBitsEvents";

export const onAddCardSubmitHandler = async (
  cardInfo: StripeCardElementChangeEvent | null,
  nameFieldValue: string,
  user: {
    email: string;
    first_name: string;
    last_name: string;
    token: string;
    id: number;
    customer_id: string | null;
  },
  elements: StripeElements | null,
  stripe: Stripe | null,
  setPaymentMethod: Function,
  setNameFieldValue: Function,
  createStripeCustomerRequest: Function,
  setCustomerId: Function,
  dispatchSetUser: Function,
  retriveCustomerPaymentMethods: Function,
  setCustomerPaymentMethods: Function
) => {
  try {
    if (!!cardInfo && !!nameFieldValue) {
      const cardElement = elements!.getElement("card");
      if (user) {
        const result = await stripe!.createPaymentMethod({
          type: "card",
          card: cardElement!,
          billing_details: {
            name: nameFieldValue,
          },
        });
        if (result) {
          if (result.paymentMethod) {
            setPaymentMethod(result);
            cardElement!.clear();
            setNameFieldValue("");
            const res = await createStripeCustomerRequest({
              user_id: user?.id,
              paymentMethod: result?.paymentMethod,
            });
            if (res) {
              setCustomerId(res.data.customer_id);
              notifySuccess(res.message);
              dispatchSetUser({
                email: user.email,
                token: user.token,
                first_name: user.first_name,
                last_name: user.last_name,
                id: user.id,
                customer_id: res.data.customer_id,
              });
              retriveCustomerPaymentMethodsHandler(
                res.data.customer_id,
                retriveCustomerPaymentMethods,
                setCustomerPaymentMethods
              );
            }
          } else if (result.error) {
            const errorCode = result.error.code;
            const formattedCode = errorCode!.split("_").join(" ");
            console.log("error:", formattedCode);
          }
        }
      }
    }
  } catch (error) {
    console.log("errors : ", error);
  }
};

export const retriveCustomerPaymentMethodsHandler = async (
  customer_id: string,
  retriveCustomerPaymentMethods: Function,
  setCustomerPaymentMethods: Function
) => {
  const res = await retriveCustomerPaymentMethods(customer_id);
  if (res) {
    setCustomerPaymentMethods(res.data.stripe_card.data);
  }
};

/**
 * Maps country code or currency name to Stripe-compatible ISO 4217 currency code
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "NZ", "US")
 * @param currencyName - Currency name (e.g., "New Zealand Dollar", "Dollar")
 * @returns ISO 4217 currency code in lowercase (e.g., "nzd", "usd")
 */
const getCurrencyCode = (countryCode?: string, currencyName?: string): string => {
  // Map country codes to currency codes
  const countryToCurrency: { [key: string]: string } = {
    NZ: "nzd", // New Zealand
    US: "usd", // United States
    AU: "aud", // Australia
    GB: "gbp", // United Kingdom
    CA: "cad", // Canada
    EU: "eur", // European Union
    JP: "jpy", // Japan
    CN: "cny", // China
    IN: "inr", // India
    SG: "sgd", // Singapore
    HK: "hkd", // Hong Kong
    KR: "krw", // South Korea
    BR: "brl", // Brazil
    MX: "mxn", // Mexico
    ZA: "zar", // South Africa
    CH: "chf", // Switzerland
    SE: "sek", // Sweden
    NO: "nok", // Norway
    DK: "dkk", // Denmark
    PL: "pln", // Poland
    TR: "try", // Turkey
    AE: "aed", // United Arab Emirates
    SA: "sar", // Saudi Arabia
  };

  // First try to get currency from country code
  if (countryCode) {
    const code = countryToCurrency[countryCode.toUpperCase()];
    if (code) return code;
  }

  // Map common currency names to ISO codes
  if (currencyName) {
    const nameLower = currencyName.toLowerCase();
    const currencyNameMap: { [key: string]: string } = {
      "new zealand dollar": "nzd",
      "nz dollar": "nzd",
      "nzd": "nzd",
      "us dollar": "usd",
      "united states dollar": "usd",
      "usd": "usd",
      "australian dollar": "aud",
      "au dollar": "aud",
      "aud": "aud",
      "british pound": "gbp",
      "pound sterling": "gbp",
      "gbp": "gbp",
      "canadian dollar": "cad",
      "ca dollar": "cad",
      "cad": "cad",
      "euro": "eur",
      "eur": "eur",
      "japanese yen": "jpy",
      "yen": "jpy",
      "jpy": "jpy",
      "chinese yuan": "cny",
      "yuan": "cny",
      "cny": "cny",
      "indian rupee": "inr",
      "rupee": "inr",
      "inr": "inr",
      "singapore dollar": "sgd",
      "sg dollar": "sgd",
      "sgd": "sgd",
      "hong kong dollar": "hkd",
      "hk dollar": "hkd",
      "hkd": "hkd",
      "dollar": "usd", // Default dollar to USD if no country context
    };

    // Check for exact matches first
    if (currencyNameMap[nameLower]) {
      return currencyNameMap[nameLower];
    }

    // Check for partial matches (e.g., "dollar" in "New Zealand Dollar")
    for (const [key, value] of Object.entries(currencyNameMap)) {
      if (nameLower.includes(key) || key.includes(nameLower)) {
        return value;
      }
    }
  }

  // Default to NZD if nothing matches
  return "nzd";
};

export const onPaymentSubmitHandler = async (
  paymentCard: StripeCard | null,
  createPaymentIntentRequest: Function,
  user: {
    email: string;
    first_name: string;
    last_name: string;
    token: string;
    id: number;
    customer_id: string | null;
  },
  stripe: Stripe | null,
  createStripePaymentRecordRequest: Function,
  addNewPurchasedBitRequest: Function,
  bitId: null | number,
  selectedCells: Set<string>,
  selectedCountry: { id: number; code?: string; price_per_bit?: number; currency_name?: string | null; currency_sort_name?: string | null; currency_symbol?: string | null } | null,
  setDisablePayment: Function,
  setPaymentCompleted: Function,
  setPaymentFailed: Function,
  setTransactionId: Function,
  setTransactionAmount: Function,
  clearSelectedBits: Function,
  setStartSelection: Function,
  setSelectionMode: Function,
  setClickedGridCell: Function,
  setGridDragged: Function,
  setSelectedCells: Function,
  setBitExpiryTime: Function,
  setBitId: Function,
  setOccupiedCells: Function,
  setEndSelection: Function,
  setShowPaymentPopUp: Function,
  setShowConfirmationPopUp: Function,
  socketRef: RefObject<WebSocket | null>,
  setBitIdToEdit: Function,
  discountedAmount?: number
) => {
  const confirmPaymentButton = document.querySelector(
    "#confirmPayment"
  ) as HTMLButtonElement;
  confirmPaymentButton!.disabled = true;

  setDisablePayment(true);
  if (paymentCard) {
    // Calculate amount and currency based on selected country
    const pixelsPerBit = 100; // Fixed: 1 Bit = 100 Pixels
    const pricePerPixel = selectedCountry?.price_per_bit || 1;
    const priceForOneBit = pricePerPixel * pixelsPerBit;
    const fullAmount = selectedCells.size * priceForOneBit;
    // Use discountedAmount when a coupon was applied; fall back to full price
    const totalAmount = (discountedAmount !== undefined && discountedAmount > 0)
      ? discountedAmount
      : fullAmount;
    
    // Get currency code using the mapping function
    // This converts currency names like "New Zealand Dollar" to ISO codes like "nzd"
    const currencyCode = getCurrencyCode(
      selectedCountry?.code,
      selectedCountry?.currency_name || undefined
    );
    
    const res = await createPaymentIntentRequest({
      user_id: user?.id,
      payment_method_id: paymentCard.id,
      amount: totalAmount,
      currency: currencyCode,
    });
    if (res) {
      if (res.data.intent.client_secret) {
        const clientSecret = res.data.intent.client_secret;
        const stripeResult = await stripe!.confirmCardPayment(clientSecret);
        if (stripeResult.error) {
          const response = await createStripePaymentRecordRequest({
            user_id: user?.id,
            payment_reference_id: res?.data?.intent?.id,
            payment_method_id: paymentCard.id,
            status: "FAIL",
            amount: totalAmount,
            currency: currencyCode,
            country_id: selectedCountry?.id,
          });
          setPaymentFailed(true);
          confirmPaymentButton!.disabled = false;
          setDisablePayment(false);
        } else if (stripeResult.paymentIntent) {
          const response = await createStripePaymentRecordRequest({
            user_id: user?.id,
            payment_reference_id: res?.data?.intent?.id,
            payment_method_id: paymentCard.id,
            status: "DONE",
            amount: totalAmount,
            currency: currencyCode,
            country_id: selectedCountry?.id,
          });
          if (response) {
            let coordinates: { row: number; col: number }[] = [];
            for (const cell of selectedCells) {
              const [row, col] = cell.split(",").map(Number);
              coordinates.push({ row: row, col: col });
            }
            const newBitResponse = await addNewPurchasedBitRequest({
              user: user.id,
              payment: response.data.payment_data.id,
              country: selectedCountry?.id || 171,
              bit: bitId,
              coordinates: coordinates,
            });
            if (newBitResponse) {
              setBitIdToEdit(newBitResponse.data.purchased_bit_id);
              dispatchCountryBitsSold(
                selectedCountry?.id ?? 171,
                coordinates.length,
              );
              setTransactionId(stripeResult.paymentIntent.id);
              setPaymentCompleted(true);
              setTransactionAmount(response.data.payment_data.amount);
              clearSelectedBits(
                setStartSelection,
                setSelectionMode,
                setClickedGridCell,
                setGridDragged,
                setSelectedCells,
                setBitExpiryTime,
                setBitId,
                setOccupiedCells,
                setEndSelection,
                setShowPaymentPopUp,
                setShowConfirmationPopUp
              );
              setDisablePayment(false);
              confirmPaymentButton!.disabled = false;
              try {
                if (socketRef.current?.readyState === WebSocket.OPEN) {
                  socketRef.current.send(
                    JSON.stringify({
                      event: "bit_purchased",
                      bit_id: newBitResponse.data.purchased_bit_id,
                    })
                  );
                } else {
                  console.warn("Socket not connected. Cannot send.");
                }
              } catch (error) {
                console.log("error : ", error);
              }
            }
          } else {
            setPaymentFailed(true);
            setDisablePayment(false);
            confirmPaymentButton!.disabled = false;
          }
        }
      } else {
        setPaymentFailed(true);
        setDisablePayment(false);
        confirmPaymentButton!.disabled = false;
      }
    } else {
      setPaymentFailed(true);
      setDisablePayment(false);
      confirmPaymentButton!.disabled = false;
    }
  }
};

export const onDeleteCardHandler = async (
  paymentMethodId: string,
  detachPaymentMethodRequest: Function,
  user: {
    email: string;
    first_name: string;
    last_name: string;
    token: string;
    id: number;
    customer_id: string | null;
  },
  retriveCustomerPaymentMethodsRequest: Function,
  setCustomerPaymentMethods: Function
) => {
  try {
    const res = await detachPaymentMethodRequest({
      payment_method_id: paymentMethodId,
    });
    if (res) {
      notifySuccess(res.message);
      if (user.customer_id) {
        retriveCustomerPaymentMethodsHandler(
          user.customer_id,
          retriveCustomerPaymentMethodsRequest,
          setCustomerPaymentMethods
        );
      }
    }
  } catch (error) {
    console.log("error : ", error);
  }
};

